import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import imageCompression from "browser-image-compression";
import { Image as ImageIcon } from "lucide-react";
import { getApiBaseUrl } from "../config/api";
import MentionInput from "./shared/MentionInput";
import CaptionAssist from "./shared/CaptionAssist";
import MediaComposePreview from "./shared/MediaComposePreview";
import { appendMentionedField } from "../utils/mentions";

const API_URL = getApiBaseUrl();

const CATEGORIES = [
  "General",
  "Really Lesson",
  "COOL",
  "ReallyAvatar",
  "Coursera",
  "TechTree",
];

const IMAGE_ACCEPT =
  "image/*,.jpg,.jpeg,.jpe,.jfif,.pjpeg,.pjp,.png,.gif,.webp,.bmp,.svg,.tif,.tiff,.avif,.heic,.heif,.ico";

const isImageFile = (file) =>
  file.type.startsWith("image/") ||
  /\.(jpe?g|jpe|jfif|pjpeg|pjp|png|gif|webp|bmp|svg|tiff?|avif|heic|heif|ico)$/i.test(
    file.name
  );

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  fontSize: 14,
  borderRadius: 12,
  border: "1px solid rgba(148, 163, 184, 0.9)",
  marginBottom: 10,
  outline: "none",
  backgroundColor: "rgba(248, 250, 252, 0.95)",
  color: "#0f172a",
  boxSizing: "border-box",
};

/**
 * Ask a learning question. Answers are regular post replies.
 * Optional photos attach as gallery media on the post.
 */
export default function QuestionForm({
  user,
  onClose,
  onSuccess,
  onRefresh,
  groupId = null,
}) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("General");
  const [mediaFiles, setMediaFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const previewUrlsRef = useRef(new Map());
  const fileInputRef = useRef(null);

  useEffect(() => {
    const activeIds = new Set(mediaFiles.map((item) => item.id));
    mediaFiles.forEach((item) => {
      if (!previewUrlsRef.current.has(item.id)) {
        previewUrlsRef.current.set(item.id, URL.createObjectURL(item.file));
      }
    });
    previewUrlsRef.current.forEach((url, id) => {
      if (!activeIds.has(id)) {
        URL.revokeObjectURL(url);
        previewUrlsRef.current.delete(id);
      }
    });
  }, [mediaFiles]);

  useEffect(() => {
    return () => {
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      previewUrlsRef.current.clear();
    };
  }, []);

  const addFiles = (files) => {
    const images = Array.from(files).filter(isImageFile);
    if (!images.length) {
      alert("Please choose photo files.");
      return;
    }
    setMediaFiles((prev) => [
      ...prev,
      ...images.map((file) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
      })),
    ]);
  };

  const removeFile = (idx) =>
    setMediaFiles((prev) => prev.filter((_, i) => i !== idx));

  const reorderFiles = (fromIndex, toIndex) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;
    setMediaFiles((prev) => {
      if (fromIndex >= prev.length || toIndex >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  };

  const moveFile = (idx, direction) => reorderFiles(idx, idx + direction);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user?.email && !user?.firstname) {
      alert("You must be logged in");
      return;
    }
    if (!title.trim() || !content.trim()) {
      alert("Please add a question title and some details.");
      return;
    }

    setLoading(true);
    setStatus("Posting your question…");
    try {
      const formData = new FormData();
      user.email && formData.append("email", user.email);
      user.firstname && formData.append("firstname", user.firstname);
      user.lastname && formData.append("lastname", user.lastname);
      user.username && formData.append("username", user.username);
      user.country && formData.append("country", user.country);
      formData.append("title", title.trim());
      appendMentionedField(formData, "content", content.trim());
      formData.append("category", category);
      formData.append("post_type", "question");
      if (groupId) {
        formData.append("group_id", groupId);
        formData.append("user_id", user.id);
      }

      const totalMedia = mediaFiles.length;
      for (let i = 0; i < totalMedia; i++) {
        const file = mediaFiles[i].file;
        try {
          setStatus(
            totalMedia > 1
              ? `Compressing photo ${i + 1} of ${totalMedia}…`
              : "Compressing photo…"
          );
          const compressed = await imageCompression(file, {
            maxSizeMB: 1.5,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
          });
          formData.append("media[]", compressed, compressed.name);
        } catch {
          formData.append("media[]", file);
        }
      }

      setStatus(totalMedia ? "Uploading…" : "Posting your question…");
      const endpoint = groupId
        ? `${API_URL}/create_group_post.php`
        : `${API_URL}/create_post.php`;
      const { data } = await axios.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });

      if (data.success) {
        onSuccess?.(data.post);
        onRefresh?.();
        onClose?.();
      } else {
        alert(data.message || "Failed to post question");
      }
    } catch (err) {
      console.error(err);
      alert("Error: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
      setStatus("");
    }
  };

  const previewItems = mediaFiles.map((item) => ({
    id: item.id,
    url: previewUrlsRef.current.get(item.id),
    isVideo: false,
  }));

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.55)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1100,
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 16,
          width: "100%",
          maxWidth: 560,
          maxHeight: "90vh",
          overflow: "auto",
          padding: "20px 22px 18px",
          boxShadow: "0 24px 48px rgba(15,23,42,0.18)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 12,
          }}
        >
          <div>
            <div
              style={{
                display: "inline-block",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.4,
                textTransform: "uppercase",
                color: "#b45309",
                background: "#fef3c7",
                padding: "3px 8px",
                borderRadius: 4,
                marginBottom: 6,
              }}
            >
              Learning Q&A
            </div>
            <h2 style={{ margin: 0, fontSize: 20, color: "#0f172a" }}>
              Ask a question
            </h2>
            <p style={{ margin: "6px 0 0", fontSize: 13, color: "#64748b" }}>
              Share a learning question. Others can answer and discuss below it.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              fontSize: 22,
              lineHeight: 1,
              cursor: "pointer",
              color: "#94a3b8",
              padding: 4,
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <label style={{ display: "block", marginBottom: 4, fontSize: 13, fontWeight: 600, color: "#334155" }}>
            Question
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. How do I start a study group for Python?"
            required
            maxLength={200}
            style={inputStyle}
          />

          <label style={{ display: "block", marginBottom: 4, fontSize: 13, fontWeight: 600, color: "#334155" }}>
            Details
          </label>
          <MentionInput
            value={content}
            onChange={setContent}
            userId={user?.id || user?.user_id}
            placeholder="Add context, what you’ve tried, or what you want to learn. Type @ to mention someone."
            required
            rows={5}
            style={{
              ...inputStyle,
              resize: "vertical",
              minHeight: 110,
              whiteSpace: "pre-wrap",
            }}
          />
          <CaptionAssist
            kind="question"
            text={content}
            title={title}
            category={category}
            userId={user?.id || user?.user_id}
            disabled={loading}
            onApply={setContent}
            onApplyTitle={(nextTitle) => {
              if (!title.trim() && nextTitle) setTitle(nextTitle);
            }}
          />

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 8,
              margin: "10px 0 8px",
            }}
          >
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                border: "1px solid #e2e8f0",
                background: "#fff",
                borderRadius: 999,
                padding: "8px 14px",
                fontWeight: 600,
                fontSize: 13,
                cursor: loading ? "wait" : "pointer",
                color: "#334155",
              }}
            >
              <ImageIcon size={15} strokeWidth={2.2} />
              Add photos
            </button>
            {mediaFiles.length > 0 && (
              <span style={{ fontSize: 12, color: "#64748b" }}>
                {mediaFiles.length} photo{mediaFiles.length === 1 ? "" : "s"} selected
              </span>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={IMAGE_ACCEPT}
            multiple
            style={{ display: "none" }}
            onChange={(e) => {
              if (e.target.files?.length) addFiles(e.target.files);
              e.target.value = "";
            }}
          />
          {previewItems.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <MediaComposePreview
                items={previewItems}
                onRemove={removeFile}
                onReorder={reorderFiles}
                onMove={moveFile}
              />
            </div>
          )}

          <label style={{ display: "block", margin: "4px 0 4px", fontSize: 13, fontWeight: 600, color: "#334155" }}>
            Topic
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            style={inputStyle}
          >
            {CATEGORIES.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>

          {status && (
            <p style={{ margin: "0 0 10px", fontSize: 13, color: "#64748b" }}>
              {status}
            </p>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                border: "1px solid #e2e8f0",
                background: "#fff",
                borderRadius: 999,
                padding: "9px 16px",
                fontWeight: 600,
                cursor: "pointer",
                color: "#475569",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                border: "none",
                background: "linear-gradient(135deg, #f59e0b, #d97706)",
                color: "#fff",
                borderRadius: 999,
                padding: "9px 18px",
                fontWeight: 700,
                cursor: loading ? "wait" : "pointer",
              }}
            >
              {loading ? "Posting…" : "Post question"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
